import {useEffect,useState} from 'react';

type Choice={id:string;name:string};
type Customer={customerId:string;displayName:string;email?:string|null;phone?:string|null};
type Wait={id:string;customerId:string;customerName:string;date:string;
  fromMinute:number;toMinute:number;resourceIds:string[];
  serviceId:string|null;serviceName:string|null;additionalGuests:number;
  priority:'LOW'|'NORMAL'|'HIGH';notes:string;notifyEmail:boolean;notifySms:boolean;
  version:number;createdAt:string;origin:'STAFF'|'PUBLIC';status?:string};
const toTime=(minutes:number)=>String(Math.floor(minutes/60)).padStart(2,'0')+':'+
  String(minutes%60).padStart(2,'0');
const fromTime=(time:string)=>Number(time.slice(0,2))*60+Number(time.slice(3,5));
const label=(date:string)=>new Intl.DateTimeFormat('en-US',{dateStyle:'full',timeZone:'UTC'})
  .format(new Date(date+'T12:00:00Z'));
export function StaffWaitlistPanel({locationCode,day,rooms,services,onClose}:{
  locationCode:string;day:string;rooms:Choice[];services:Choice[];
  onClose:()=>void;
}){
  const [history,setHistory]=useState(false);
  const [showForm,setShowForm]=useState(false);
  const [editing,setEditing]=useState<{id:string;version:number}|null>(null);
  const [entries,setEntries]=useState<Wait[]>([]);
  const [busy,setBusy]=useState(false);
  const [loading,setLoading]=useState(true);
  const [message,setMessage]=useState('');
  const [reload,setReload]=useState(0);
  const [query,setQuery]=useState('');
  const [customerResults,setCustomerResults]=useState<Customer[]>([]);
  const [customer,setCustomer]=useState<Customer|null>(null);
  const [guests,setGuests]=useState(0);
  const [chosenRooms,setChosenRooms]=useState<string[]>([]);
  const [serviceId,setServiceId]=useState('');
  const [from,setFrom]=useState('10:00');
  const [to,setTo]=useState('21:59');
  const [priority,setPriority]=useState<'LOW'|'NORMAL'|'HIGH'>('NORMAL');
  const [notes,setNotes]=useState('');
  const [notifyEmail,setNotifyEmail]=useState(false);
  const [notifySms,setNotifySms]=useState(false);
  useEffect(()=>{
    const ctrl=new AbortController();
    setLoading(true);
    void fetch('/api/v1/staff/waitlist?'+new URLSearchParams({
      location:locationCode,date:day,...(history?{history:'true'}:{}),
    }),{credentials:'same-origin',cache:'no-store',signal:ctrl.signal})
      .then(async r=>{
        const body=await r.json() as {ok:boolean;data?:{entries:Wait[]}};
        if(!r.ok||!body.ok||!body.data)throw Error('Waitlist unavailable.');
        if(!ctrl.signal.aborted){setEntries(body.data.entries);setMessage('');}
      }).catch(()=>{if(!ctrl.signal.aborted)setMessage('Waitlist unavailable.')})
      .finally(()=>{if(!ctrl.signal.aborted)setLoading(false)});
    return ()=>ctrl.abort();
  },[locationCode,day,history,reload]);
  useEffect(()=>{
    if(query.trim().length<2||customer){setCustomerResults([]);return;}
    const ctrl=new AbortController();
    const timer=setTimeout(()=>{
      void fetch('/api/v1/staff/customers?'+new URLSearchParams({
        location:locationCode,q:query.trim(),
      }),{credentials:'same-origin',cache:'no-store',signal:ctrl.signal})
        .then(async r=>{
          const body=await r.json() as {ok:boolean;data?:{customers:Customer[]}};
          if(r.ok&&body.ok&&!ctrl.signal.aborted)
            setCustomerResults(body.data?.customers??[]);
        }).catch(()=>{if(!ctrl.signal.aborted)setCustomerResults([])});
    },200);
    return ()=>{ctrl.abort();clearTimeout(timer)};
  },[query,customer,locationCode]);
  const reset=()=>{
    setShowForm(false);setEditing(null);setQuery('');setCustomer(null);setGuests(0);
    setChosenRooms([]);setServiceId('');setFrom('10:00');setTo('21:59');
    setPriority('NORMAL');setNotes('');setNotifyEmail(false);setNotifySms(false);
  };
  const edit=(row:Wait)=>{
    setEditing({id:row.id,version:row.version});setShowForm(true);
    setCustomer({customerId:row.customerId,displayName:row.customerName});
    setQuery(row.customerName);setGuests(row.additionalGuests);
    setChosenRooms(row.resourceIds);setServiceId(row.serviceId??'');
    setFrom(toTime(row.fromMinute));setTo(toTime(row.toMinute));
    setPriority(row.priority);setNotes(row.notes);
    setNotifyEmail(row.notifyEmail);setNotifySms(row.notifySms);
  };
  const submit=async()=>{
    if(!customer)return;
    setBusy(true);setMessage('');
    try{
      const response=await fetch(editing?
        '/api/v1/staff/waitlist/'+encodeURIComponent(editing.id):
        '/api/v1/staff/waitlist',{
        method:editing?'PATCH':'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,
          ...(editing?{version:editing.version}:{}),draft:{
          customerId:customer.customerId,date:day,fromMinute:fromTime(from),
          toMinute:fromTime(to),resourceIds:chosenRooms,serviceId:serviceId||null,
          additionalGuests:guests,priority,notes,notifyEmail,notifySms,
        }}),
      });
      const body=await response.json() as {ok:boolean;error?:{code:string}};
      if(!response.ok||!body.ok)throw Error(body.error?.code??'Unable to add to waitlist');
      reset();setReload(x=>x+1);
    }catch(e){setMessage(e instanceof Error?e.message:'Could not add to waitlist')}
    finally{setBusy(false)}
  };
  const remove=async(row:Wait)=>{
    if(busy)return;setBusy(true);setMessage('');
    try{
      const response=await fetch('/api/v1/staff/waitlist/'+encodeURIComponent(row.id),{
        method:'DELETE',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,version:row.version}),
      });
      if(!response.ok)throw Error('Could not remove waitlist entry.');
      setReload(x=>x+1);
    }catch(e){setMessage(e instanceof Error?e.message:'Could not remove waitlist entry.')}
    finally{setBusy(false)}
  };
  return <div className="ecfs-waitlist-backdrop" onClick={onClose}>
    <section role="dialog" aria-modal="true" aria-label="Staff Waitlist"
      className="ecfs-waitlist-panel" onClick={event=>event.stopPropagation()}>
      <div className="ecfs-waitlist-heading">
        <h2>Waitlist for {label(day)}</h2>
        <button type="button" className="ecfs-link" onClick={()=>setHistory(v=>!v)}>
          {history?'current waitlist':'waitlist history'}</button>
        <button type="button" aria-label="Close waitlist" onClick={onClose}>×</button>
      </div>
      <table className="ecfs-waitlist-table"><thead><tr>
        <th>Customer</th><th>Guests</th><th>Time</th><th>Rooms</th>
        <th>Service</th><th>Added</th><th>Contact</th><th>Actions</th>
      </tr></thead><tbody>
        {entries.map(row=><tr key={row.id}>
          <td>{row.customerName}</td><td>{row.additionalGuests}</td>
          <td>{row.date} {toTime(row.fromMinute)}–{toTime(row.toMinute)}</td>
          <td>{row.resourceIds.length?row.resourceIds.map(id=>
            rooms.find(r=>r.id===id)?.name??'Room').join(', '):'Any room'}</td>
          <td>{row.serviceName??'Any service'}</td>
          <td>{new Date(row.createdAt).toLocaleDateString('en-US')}</td>
          <td title="Notification preferences are recorded but delivery is not enabled">
            {row.notifyEmail?'Email ':''}{row.notifySms?'Text ':''}
            {!row.notifyEmail&&!row.notifySms?'None':''}
          </td>
          <td><button type="button" disabled={busy||history}
            aria-label={'Edit '+row.customerName+' waitlist entry'}
            onClick={()=>edit(row)}>Edit</button>
            <button type="button" disabled={busy||history}
            aria-label={'Remove '+row.customerName+' from waitlist'}
            onClick={()=>void remove(row)}>Remove</button></td>
        </tr>)}
      </tbody></table>
      {loading&&<p role="status">Loading waitlist…</p>}
      {!loading&&!entries.length&&<p>No entries for this date.</p>}
      {!history&&<>
        <button type="button" className="ecfs-link" onClick={()=>setShowForm(x=>!x)}>
          {showForm?'Cancel':editing?'Edit waitlist entry':'add customer to waitlist'}</button>
        {showForm&&<form className="ecfs-waitlist-form" onSubmit={e=>{
          e.preventDefault();void submit();
        }}>
          <label>Customer
            {customer?<strong>{customer.displayName}
              <button type="button" onClick={()=>{setCustomer(null);setQuery('')}}>change</button>
            </strong>:<input aria-label="Find Waitlist Customer" value={query}
              placeholder="Find Customer" onChange={e=>setQuery(e.target.value)}/>}
          </label>
          {!customer&&customerResults.length>0&&<div className="ecfs-waitlist-search-results">
            {customerResults.map(person=><button type="button" key={person.customerId}
              onClick={()=>{setCustomer(person);setQuery(person.displayName)}}>
              {person.displayName}</button>)}
          </div>}
          <label>with <input aria-label="Additional guests" type="number" min={0} max={20}
            value={guests} onChange={e=>setGuests(Number(e.target.value))}/> guests</label>
          <fieldset><legend>Rooms</legend>
            <label><input type="checkbox" checked={!chosenRooms.length}
              onChange={()=>setChosenRooms([])}/>Any room</label>
            {rooms.map(room=><label key={room.id}><input type="checkbox"
              checked={chosenRooms.includes(room.id)}
              onChange={e=>setChosenRooms(prev=>e.target.checked?
                [...prev,room.id]:prev.filter(id=>id!==room.id))}/>
              {room.name}</label>)}
          </fieldset>
          <label>Service <select aria-label="Waitlist service" value={serviceId}
            onChange={e=>setServiceId(e.target.value)}>
            <option value="">Any Service</option>
            {services.map(service=><option key={service.id} value={service.id}>
              {service.name}</option>)}
          </select></label>
          <div className="ecfs-waitlist-time-row">
            <label>From <input type="time" aria-label="Waitlist start time"
              value={from} onChange={e=>setFrom(e.target.value)}/></label>
            <label>to <input type="time" aria-label="Waitlist end time"
              value={to} onChange={e=>setTo(e.target.value)}/></label>
          </div>
          <label>Priority <select value={priority} onChange={e=>
            setPriority(e.target.value as typeof priority)}>
            <option value="LOW">Low</option><option value="NORMAL">Normal</option>
            <option value="HIGH">High</option>
          </select></label>
          <textarea aria-label="Waitlist notes" placeholder="Notes" value={notes}
            onChange={e=>setNotes(e.target.value)}/>
          <label><input type="checkbox" checked={notifyEmail}
            onChange={e=>setNotifyEmail(e.target.checked)}/> Notify by email when enabled</label>
          <label><input type="checkbox" checked={notifySms}
            onChange={e=>setNotifySms(e.target.checked)}/> Notify by text when enabled</label>
          <p>Notification delivery is not yet connected. These preferences are saved
            but messages will not be sent automatically.</p>
          <button type="submit" disabled={busy||!customer||fromTime(to)<=fromTime(from)}>
            {busy?'Saving…':editing?'Save Waitlist Changes':'Add To Waitlist'}</button>
        </form>}
      </>}
      {message&&<p role="alert">{message}</p>}
    </section>
  </div>;
}
